import { css, html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import './components/Button';
import { closeIcon } from './shared/icons/close';
import { MediaFile } from './types';

@customElement('select-file-dialog')
export class SelectFileDialog extends LitElement {
  static readonly styles = css`
    :host {
      font-family: Roboto, sans-serif;
      --media-picker-primary-color: #3f51b5;
      --media-picker-on-primary-color: #fff;
      --media-picker-spacing-vertical: 12px;
      --media-picker-spacing-horizontal: 24px;
      font-family: Roboto, sans-serif;
    }

    * {
      box-sizing: border-box;
    }

    dialog {
      position: fixed;
      display: flex;
      flex-direction: column;
      inset: 0;
      transition: all 0.5s;
      padding: 0;
      box-shadow: 0 0 2rem 0 rgba(0, 0, 0, 0.5);
      background-color: #f7f9ff;
      color: #000;
      width: 100%;
      height: 100%;
      max-width: 90%;
      max-height: 90%;
      border: none;
      transform: scale(0.9);
      border-radius: 12px;
      overflow: hidden;
      z-index: 2;
    }

    dialog[open] {
      animation: 0.5s cubic-bezier(0.35, 0.25, 0, 0.72) 0s 1 normal forwards
        running show-dialog;
    }

    dialog:not([open]) {
      display: none;
      pointer-events: none;
      opacity: 0;
    }

    dialog::backdrop {
      background-color: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(1px);
    }

    dialog header {
      margin: 0;
      padding: 0 24px;
      color: var(--media-picker-on-primary-color);
      background-color: var(--media-picker-primary-color);
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 64px;
    }

    dialog header h3 {
      margin: 0;
      margin-block-start: 0;
      margin-block-end: 0;
    }

    dialog header .dialog-header-actions {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
    }

    dialog .dialog-content {
      flex: 1;
      overflow: auto;
    }

    dialog footer {
      height: 64px;
      padding: 12px 24px;
      display: flex;
      align-items: center;
      gap: 24px;
      border-top: 1px solid #e5e7eb;
      background-color: #f7f9ff;
    }

    dialog .dialog-content .form {
      display: flex;
      flex-direction: row;
      gap: 24px;
      flex-direction: row;
    }

    dialog .dialog-content .form .file-preview {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    dialog .dialog-close {
      position: absolute;
      top: 12px;
      right: 12px;
      cursor: pointer;
      z-index: 3;
    }

    @keyframes show-dialog {
      100% {
        transform: scale(1);
      }
    }

    .flex-1 {
      flex: 1;
    }
  `;

  @property({ type: Number }) maxFileSelected: number | null = null;

  @query('dialog') _dialog!: HTMLDialogElement;

  @state() selectedFile: MediaFile[] = [];

  public showModal() {
    this._dialog.showModal();
  }

  public close() {
    this.selectedFile = [];
    this._dialog.close();
  }

  private _handleSelectionChange(event: CustomEvent) {
    this.selectedFile = event.detail.files;
  }

  private _onFileUploaded(event: CustomEvent) {
    this.selectedFile = event.detail.files;

    this._confirmSelection(
      new CustomEvent('internal-event', {
        detail: {
          files: this.selectedFile,
        },
      })
    );
  }

  private _confirmSelection(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('file-selected', {
        detail: {
          files: this.selectedFile,
        },
      })
    );

    this.close();
  }

  render() {
    return html`
      <dialog>
        <header>
          <h3>
            Select File
            ${this.maxFileSelected ? `(max ${this.maxFileSelected})` : ''}
          </h3>
          <div class="dialog-close">
            <icon-button
              .icon=${closeIcon}
              @click="${this.close.bind(this)}"
            ></icon-button>
          </div>
        </header>
        <div class="dialog-content">
          <media-file-list
            .maxFileSelected=${this.maxFileSelected}
            @selection-changed="${this._handleSelectionChange.bind(this)}"
            @files-uploaded="${this._onFileUploaded.bind(this)}"
          ></media-file-list>
        </div>
        <footer>
          <media-picker-button
            variant="secondary"
            label="Cancel"
            @click="${this.close.bind(this)}"
          ></media-picker-button>
          <span class="flex-1"></span>
          <media-picker-button
            variant="primary"
            label="Select files"
            @click="${this._confirmSelection.bind(this)}"
          ></media-picker-button>
        </footer>
      </dialog>
    `;
  }
}
